<script>
  import { onDestroy } from "svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { createRequestScope } from "lib/util/requestScope.js";
  import { filtersConfigStore } from "@stores/damFiltersConfigStores.js";
  import { getApplicableFilters } from "@utils/getDamApplicableFilters.js";
  import { damfilters } from "lib/stores.js";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";

  // ui-elements
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import Toggle from "lib/components/Toggle.svelte";
  import { Accordion, SectionWrapper, Button } from "paxray-components-svelte";
  import AppSelection from "./components/appSelection.svelte";
  import AppInformation from "./components/appInformation.svelte";
  import Header from "lib/components/Header.svelte";
  import { Loading } from "carbon-components-svelte";

  import Filter from "./components/filter/Filter.svelte";
  import AppDrilldownSourceMenu from "./components/AppDrilldownSourceMenu.svelte";
  import MatrixTable from "./components/MatrixTable.svelte";
  import ButtonNumberInput from "../../../../lib/components/ButtonNumberInput.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import FlowPlotterSmall from "lib/components/FlowPlotterSmall.svelte";
  import {
    BOT_WHITE_ICON,
    FULLSCREEN_ICON,
    FULLSCREEN_OFF_ICON,
    TABLE_ICON,
    TRANSITIONS_ICON,
  } from "@utils/icons.js";

  // Requests der Maske enden, wenn die Maske verlassen wird.
  const abortSignal = createRequestScope();
  const navigate = useNavigate();
  export let idproject;
  const { setCurrentView } = filtersConfigStore;

  let currentApp = "";
  let windowPartitionsLoading = false;
  let applicationlist = [];
  let windowlist = [];
  let fullscreen = false;
  let selectedLevel = 0;
  let previousSelectedLevel = 0;
  let selectedIndex = 0;
  let selectedSourceIndex = 0;
  let loading = true;
  let loadingApp = true;
  let view = "Shortcuts";
  let frequency = 100;
  let appliedFrequency = frequency;
  // Copy-Paste startet mit Schwelle 1 (alles sichtbar); die 100 gilt den
  // Shortcut-Ansichten, die ihre Schwelle aus den Daten setzen.
  let copyFrequencyInit = false;
  $: if (selectedIndex === 0 && !copyFrequencyInit) {
    copyFrequencyInit = true;
    frequency = 1;
  }
  let automaticFrequencyContext = "";
  let frequencyDebounceTimeout;
  // Threshold the window level was thinned with; 0 while the app level shows.
  let windowMinCount = 0;
  // Hat der Nutzer die Schwelle selbst gesetzt? Dann ueberschreibt die
  // Automatik sie nicht mehr, solange er auf der Fenstertitel-Ebene bleibt.
  let windowMinTouched = false;
  // Beschriftung der Sammelzeile und -spalte. Beide sind keine Fenster, also
  // fuehrt aus ihnen auch kein Klick weiter.
  const OTHERS = "Others";
  // Worauf der Nutzer eingetaucht ist - noetig, um einen Klick auf "Others"
  // zurueckzunehmen, ohne die Ebene zu verlassen.
  let drilledFrom = "";
  let drilledTo = "";
  let lastDataLoadKey = "";
  let lastAppLoadKey = "";
  let paxbotOpen = false;
  let analysisView = "table";
  let zoomLevel = 100;
  let copyUserCoverage = 0;

  $: setCurrentView(view);
  $: selectionData = selectedLevel === 1 ? windowlist : applicationlist;
  $: copyTimeSummary = getCopyTimeSummary(applicationlist);
  $: copyGraph = getCopyGraph(data, selectedLevel, selectionData, currentApp);
  $: if (selectedLevel !== previousSelectedLevel) {
    previousSelectedLevel = selectedLevel;
    currentApp = "";
    selectedFrom = "";
    selectedTo = "";
  }
  $: if (analysisView === "flow" && !currentApp && selectionData.length > 0) {
    currentApp = [...selectionData].sort(
      (left, right) =>
        (Number(right.copy_time) || 0) - (Number(left.copy_time) || 0),
    )[0].id;
  }
  $: copyTimeSummaryItems = [
    {
      label: "Total Copy Time",
      value: formatPaxbotDuration(copyTimeSummary.totalCopyTime),
    },
    {
      label: "Avg. Copy Time",
      value: `${formatPercentage(copyTimeSummary.averageCopyTimeShare)}%`,
    },
    {
      label: "Copy Users",
      value: `${formatPercentage(copyUserCoverage)}%`,
    },
    {
      label: "Top10 Copy Time",
      value: `${formatPercentage(copyTimeSummary.top10CopyTimeShare)}%`,
    },
  ];

  let from = "";
  let to = "";
  let selectedFrom = from;
  let selectedTo = to;
  let tableHeader;
  let dataTable = [];
  let highValueCells = new Set();
  let warningValueCells = new Set();
  let cellTooltips = new Map();

  // Keep the matrix row selection in sync with the application selected in
  // the sidebar when the copy-paste view is active. The block must react to a
  // changed sidebar app only - it also mentions selectedFrom/selectedTo, so
  // without syncedApp it re-ran on every matrix click and wiped the cell
  // selection before the window-title level could be built.
  // Plattform-Feature "Main: Window Titles & UI Elements": aus -> Hauptphase
  // bleibt auf der Anwendungsebene (keine Fenster-Matrix, kein Fenster-Flow).
  $: windowTitlesLocked =
    selectedSourceIndex === 1 &&
    !$uiFeatureStore.platformFeatures.mainWindowTitles;
  $: if (windowTitlesLocked && selectedLevel !== 0) selectedLevel = 0;
  $: if (windowTitlesLocked && (selectedFrom || selectedTo)) {
    selectedFrom = "";
    selectedTo = "";
    copyPasteTable();
  }
  let syncedApp = null;
  $: if (selectedIndex === 0 && currentApp !== syncedApp) {
    syncedApp = currentApp;
    if (currentApp && !windowTitlesLocked) {
      selectedFrom = currentApp;
      selectedTo = "";
      fiterCopyPaste();
    } else if (selectedFrom) {
      selectedFrom = "";
      selectedTo = "";
      copyPasteTable();
    }
  }

  let data = [];
  $: noDataForFilters = !loading && data.length === 0;

  $: {
    if ($damfilters && $filtersConfigStore.storeLoaded) {
      const applicableFilters = getCurrentFilters();
      const loadKey = JSON.stringify({
        idproject,
        selectedIndex,
        selectedSourceIndex,
        currentApp,
        selectedLevel,
        ...(selectedIndex === 0 ? {} : { appliedFrequency }),
        filters: applicableFilters,
      });
      if (loadKey !== lastDataLoadKey) {
        lastDataLoadKey = loadKey;
        getData(selectedIndex, applicableFilters);
      }
    }
  }

  $: {
    if ($damfilters && $filtersConfigStore.storeLoaded) {
      const applicableFilters = getCurrentFilters();
      const loadKey = JSON.stringify({
        idproject,
        selectedSourceIndex,
        filters: applicableFilters,
      });
      if (loadKey !== lastAppLoadKey) {
        lastAppLoadKey = loadKey;
        getWindowPartitions(applicableFilters);
      }
    }
  }

  $: if (selectedIndex !== 0 && currentApp) {
    selectedTo = currentApp;
  }

  $: if (selectedFrom || selectedTo) {
    filterDataTable();
  }

  function dataSource() {
    return selectedSourceIndex === 1 ? "main" : "recordings";
  }

  function getCurrentFilters() {
    try {
      return getApplicableFilters($damfilters[idproject]);
    } catch (error) {
      return [];
    }
  }

  function getWindowPartitions(applicableFilters = getCurrentFilters()) {
    windowPartitionsLoading = true;
    loadingApp = true;
    const baseUrl = `/api/damprojects`;
    const params = new URLSearchParams({
      project_id: idproject,
      filter: JSON.stringify(applicableFilters),
      source: dataSource(),
    });
    Promise.all([
      fetchWithSession(`${baseUrl}/getApplicationKPIs?${params}`, {
        signal: abortSignal,
      }).then((res) => res.json()),
      fetchWithSession(`${baseUrl}/getWindowTitleKPIs?${params}`, {
        signal: abortSignal,
      }).then((res) => res.json()),
    ])
      .then(([applications, windows]) => {
        applicationlist = applications;
        windowlist = windows;
        windowPartitionsLoading = false;
        loadingApp = false;
      })
      .catch(() => {
        windowPartitionsLoading = false;
        loadingApp = false;
      });
  }

  function getData(selectedIndex, applicableFilters = getCurrentFilters()) {
    if (!$filtersConfigStore.storeLoaded) return;
    loading = true;
    const filterString = JSON.stringify(applicableFilters);
    let endpoint = "";
    if (selectedIndex === 0) {
      endpoint = `/api/damprojects/copyPasteAnalysis?project_id=`;
    } else if (selectedIndex === 1) {
      endpoint = `/api/damprojects/usedShortCutAnalysis?project_id=`;
    } else {
      endpoint = `/api/damprojects/missedShortCutAnalysis?project_id=`;
    }
    const params = new URLSearchParams({
      project_id: idproject,
      mincount: String(selectedIndex === 0 ? 0 : appliedFrequency),
      filter: filterString,
      source: dataSource(),
    });
    if (selectedLevel && currentApp) params.set("partitionlabel", currentApp);
    fetchWithSession(`${endpoint.split("?")[0]}?${params.toString()}`, {
      signal: abortSignal,
    })
      .then(async (res) => ({
        data: await res.json(),
        total: Number(res.headers.get("X-Total-Count") || 0),
        copyUserCoverage: Number(res.headers.get("X-Copy-User-Coverage") || 0),
      }))
      .then(({ data: dataRes, total, copyUserCoverage: userCoverage }) => {
        data = dataRes;
        copyUserCoverage = userCoverage;
        if (
          selectedIndex !== 0 &&
          automaticFrequencyContext !== automaticContext &&
          total > 0
        ) {
          automaticFrequencyContext = automaticContext;
          // Startschwelle: 0,5 % der Paare, hoechstens 3 (Anzeige ab 3 Vorkommen);
          // bei Main-Daten (~8.700 Paare) waere die reine Prozentregel 44.
          const automaticFrequency = Math.min(
            3,
            Math.max(1, Math.ceil(total * 0.005)),
          );
          frequency = automaticFrequency;
          if (appliedFrequency !== automaticFrequency) {
            appliedFrequency = automaticFrequency;
            return;
          }
        }
        getInitialDataTable();
        // Re-apply the row selection after the data request has rebuilt the
        // matrix; otherwise the fresh table replaces the clicked row filter.
        if (selectedIndex === 0 && selectedFrom) {
          fiterCopyPaste();
        }
        loading = false;
      });
  }

  function shortcutTable() {
    tableHeader = ["Shortcut/App"];
    dataTable = [];
    cellTooltips = new Map();

    // Get unique values
    const shortcuts = Array.from(new Set(data.map((item) => item.shortcut)));
    const apps = Array.from(new Set(data.map((item) => item.app)));

    // Add application columns to the header
    tableHeader.push(...apps);
    tableHeader.push("Total");

    // Init total by columns
    let columnTotals = Array(apps.length).fill(0);
    let grandTotal = 0;

    const rows = shortcuts.map((shortcut) => {
      const row = [shortcut];
      let rowTotal = 0;

      apps.forEach((app, idx) => {
        const count = data
          .filter((item) => item.shortcut === shortcut && item.app === app)
          .reduce((sum, item) => sum + item.count, 0);

        row.push(count > 0 ? count : "");

        columnTotals[idx] += count;
        rowTotal += count;
      });

      row.push(rowTotal);
      grandTotal += rowTotal;

      return row;
    });
    const totalRow = ["Total"];
    columnTotals.forEach((total) => totalRow.push(total));
    totalRow.push(grandTotal);

    dataTable = [...rows, totalRow];
    updateValueHighlights();
  }

  function copyPasteTable() {
    tableHeader = ["From/to"];
    dataTable = [];
    const nextCellTooltips = new Map();
    const nodeLabel = (item, direction) =>
      selectedLevel === 1
        ? `${item[`${direction}_app`]} - ${item[`${direction}_window`]}`
        : item[`${direction}_app`];

    const transitionTotals = new Map();
    data.forEach((item) => {
      const key = `${nodeLabel(item, "from")}\x1f${nodeLabel(item, "to")}`;
      transitionTotals.set(
        key,
        (transitionTotals.get(key) || 0) + (Number(item.count) || 0),
      );
    });
    const filteredData = data.filter((item) => {
      const key = `${nodeLabel(item, "from")}\x1f${nodeLabel(item, "to")}`;
      return (transitionTotals.get(key) || 0) >= Number(frequency);
    });

    // Extract all unique combinations
    const toApps = Array.from(
      new Set(filteredData.map((item) => nodeLabel(item, "to"))),
    );
    const fromApps = Array.from(
      new Set(filteredData.map((item) => nodeLabel(item, "from"))),
    );

    // Add to_app columns to the header
    tableHeader.push(...toApps);
    tableHeader.push("Total");

    // Initialize structure to populate the table
    let rows = [];
    let countColumns = ["Total", ...Array(toApps.length).fill(0), 0];

    fromApps.forEach((fromApp, rowIndex) => {
      let row = [fromApp];
      let rowTotal = 0;

      toApps.forEach((toApp, idx) => {
        const cellTransitions = filteredData.filter(
          (item) =>
            nodeLabel(item, "from") === fromApp &&
            nodeLabel(item, "to") === toApp,
        );
        const totalCount = cellTransitions.reduce(
          (sum, item) => sum + (Number(item.count) || 0),
          0,
        );

        row.push(totalCount > 0 ? totalCount : "");
        const tooltip = getCopyPasteTooltip(cellTransitions, selectedLevel);
        if (tooltip.lines.length) {
          nextCellTooltips.set(`${rowIndex}:${idx + 1}`, tooltip);
        }

        // Accumulate totals
        rowTotal += totalCount;
        countColumns[idx + 1] += totalCount;
      });

      row.push(rowTotal);
      countColumns[countColumns.length - 1] += rowTotal;

      rows.push(row);
    });

    dataTable = [...rows, countColumns];
    cellTooltips = nextCellTooltips;
    updateValueHighlights();
  }

  function cleanTransitionLabel(value, fallback) {
    const label = String(value || "")
      .replaceAll("<<<", "")
      .replaceAll(">>>", "")
      .trim();
    return label || fallback;
  }

  // Plattform-Feature "Registrations: UI Elements": aus -> in den Registrierungen
  // kein Mouse-over mit Bedienelementen (Fenstertitel bleiben).
  $: elementsLocked =
    selectedSourceIndex === 0 &&
    !$uiFeatureStore.platformFeatures.registrationUiElements;

  function getCopyPasteTooltip(transitions, level) {
    // Toggle 'Main: Window Titles & UI Elements' aus: kein Mouse-over mit
    // Fenstertiteln oder Elementen in der Hauptphase
    if (windowTitlesLocked) return { title: "", lines: [], rows: [] };
    if (level === 1 && elementsLocked)
      return { title: "", lines: [], rows: [] };
    const counts = new Map();
    transitions.forEach((transition) => {
      const from = cleanTransitionLabel(
        level === 1 ? transition.from_element : transition.from_window,
        level === 1 ? "Unknown element" : "Untitled window",
      );
      const to = cleanTransitionLabel(
        level === 1 ? transition.to_element : transition.to_window,
        level === 1 ? "Unknown element" : "Untitled window",
      );
      const key = `${from}\x1f${to}`;
      const item = counts.get(key) || { from, to, count: 0 };
      item.count += Number(transition.count) || 0;
      counts.set(key, item);
    });
    const sorted = [...counts.values()].sort(
      (left, right) => right.count - left.count,
    );
    const maxCount = Number(sorted[0]?.count) || 0;
    const limit = level === 1 ? 10 : 6;
    const rows = sorted
      .filter((item) => item.count >= maxCount * 0.1)
      .slice(0, limit);
    return {
      title: level === 1 ? "Top Elements" : "Top Window Transitions",
      lines: rows.map((item) => `${item.from} -- ${item.count}x -> ${item.to}`),
      rows: rows.map((item) => ({
        count: item.count,
        label: `${item.from} -> ${item.to}`,
      })),
    };
  }

  function getInitialDataTable() {
    if (selectedIndex === 0) {
      copyPasteTable();
    } else {
      shortcutTable();
    }
  }

  function fiterCopyPaste() {
    if (selectedTo === "From/to") {
      windowMinCount = 0;
      windowMinTouched = false;
      selectedFrom = "";
      selectedTo = "";
      getInitialDataTable();
      return;
    }

    if (selectedFrom === OTHERS || selectedTo === OTHERS) {
      if (selectedFrom === OTHERS) selectedFrom = drilledFrom;
      if (selectedTo === OTHERS) selectedTo = drilledTo;
      return;
    }

    let previousTableHeader = [...tableHeader];
    let previousDataTable = [...dataTable];
    let previousCellTooltips = cellTooltips;
    let matches = [];
    let tableFrom = [];
    const nextCellTooltips = new Map();
    // Was die Schwelle wegnimmt, wird nach seinem verbliebenen Anker sortiert:
    // steht die Quelle noch als Zeile, faellt es in deren Others-Spalte; steht
    // nur noch das Ziel, in die Others-Zeile unter dessen Spalte; ist beides
    // fort, in die Ecke, wo Zeile und Spalte sich treffen.
    const othersByFrom = new Map();
    const othersByTo = new Map();
    let othersCorner = 0;
    tableHeader = ["From/to"];
    dataTable = [];
    cellTooltips = new Map();

    // Option 1: only selectedTo (click on column header)
    if (selectedTo && !selectedFrom) {
      to = selectedTo;

      data.forEach((element) => {
        if (element.to_app === to) {
          if (!tableHeader.includes(element.to_window)) {
            tableHeader.push(element.to_window);
          }
          if (!tableFrom.includes(element.from_app)) {
            tableFrom.push(element.from_app);
          }
          matches.push({
            from_window: element.from_app,
            to_window: element.to_window,
            count: element.count,
            source: element,
          });
        }
      });
    }

    // Option 2: only selectedFrom (click on row header)
    else if (selectedFrom && !selectedTo) {
      from = selectedFrom;

      data.forEach((element) => {
        if (element.from_app === from) {
          if (!tableHeader.includes(element.to_app)) {
            tableHeader.push(element.to_app);
          }
          if (!tableFrom.includes(element.from_window)) {
            tableFrom.push(element.from_window);
          }
          matches.push({
            from_window: element.from_window,
            to_window: element.to_app,
            count: element.count,
            source: element,
          });
        }
      });
    }

    // Option 3: both selectedFrom and selectedTo (click on cell)
    else if (selectedFrom && selectedTo) {
      from = selectedFrom;
      to = selectedTo;

      data.forEach((element) => {
        if (element.from_app === from && element.to_app === to) {
          if (!tableHeader.includes(element.to_window)) {
            tableHeader.push(element.to_window);
          }
          if (!tableFrom.includes(element.from_window)) {
            tableFrom.push(element.from_window);
          }
          matches.push({
            from_window: element.from_window,
            to_window: element.to_window,
            count: element.count,
            source: element,
          });
        }
      });
    }

    // Window level can open into hundreds of rows and columns. Thin it with
    // the same relative rule the shortcut view already applies to its own
    // request: everything below 0.5% of the shown total drops out.
    if (matches.length) {
      const matchTotal = matches.reduce((sum, item) => sum + item.count, 0);
      const automatik = Math.max(1, Math.ceil(matchTotal * 0.005));
      const threshold =
        windowMinTouched && windowMinCount > 0 ? windowMinCount : automatik;
      const kept = matches.filter((item) => item.count >= threshold);
      if (kept.length) {
        windowMinCount = threshold;
        const keptFrom = new Set(kept.map((item) => item.from_window));
        const keptTo = new Set(kept.map((item) => item.to_window));
        matches
          .filter((item) => item.count < threshold)
          .forEach((item) => {
            if (keptFrom.has(item.from_window)) {
              othersByFrom.set(
                item.from_window,
                (othersByFrom.get(item.from_window) || 0) + item.count,
              );
            } else if (keptTo.has(item.to_window)) {
              othersByTo.set(
                item.to_window,
                (othersByTo.get(item.to_window) || 0) + item.count,
              );
            } else {
              othersCorner += item.count;
            }
          });
        matches = kept;
        tableHeader = [
          "From/to",
          ...new Set(matches.map((item) => item.to_window)),
        ];
        tableFrom = [...new Set(matches.map((item) => item.from_window))];
      }
    }

    if (matches.length === 0) {
      dataTable = previousDataTable;
      tableHeader = previousTableHeader;
      cellTooltips = previousCellTooltips;
      updateValueHighlights();
      return;
    }

    // build the table
    const hasOthersColumn = othersByFrom.size > 0 || othersCorner > 0;
    const hasOthersRow = othersByTo.size > 0 || othersCorner > 0;
    if (hasOthersColumn) tableHeader.push(OTHERS);
    tableHeader.push("Total");
    const valueColumns = tableHeader.length - (hasOthersColumn ? 2 : 1);
    let countColumns = ["Total", ...Array(tableHeader.length - 2).fill(0), 0];

    tableFrom.forEach((fromWin, rowIndex) => {
      let count = 0;
      let row = [fromWin];
      for (let i = 1; i < valueColumns; i++) {
        const toWin = tableHeader[i];
        const valueList = matches.filter(
          (item) => item.from_window === fromWin && item.to_window === toWin,
        );
        const c = valueList.reduce((sum, item) => sum + item.count, 0);
        row.push(c > 0 ? c : "");
        const tooltip = getCopyPasteTooltip(
          valueList.map((item) => item.source),
          1,
        );
        if (c > 0 && tooltip.lines.length) {
          nextCellTooltips.set(`${rowIndex}:${i}`, tooltip);
        }
        count += c;
        countColumns[i] += c;
      }
      if (hasOthersColumn) {
        const others = othersByFrom.get(fromWin) || 0;
        row.push(others > 0 ? others : "");
        count += others;
        countColumns[valueColumns] += others;
      }
      row.push(count);
      countColumns[countColumns.length - 1] += count;
      dataTable.push(row);
    });

    if (hasOthersRow) {
      // Quellfenster, die vollstaendig unter die Schwelle fielen, haben keine
      // eigene Zeile mehr. Sie bekommen eine gemeinsame, direkt ueber der
      // Summenzeile - sonst waere ihre Menge nur noch als Differenz sichtbar.
      let othersRow = [OTHERS];
      let rowTotal = 0;
      for (let i = 1; i < valueColumns; i++) {
        const value = othersByTo.get(tableHeader[i]) || 0;
        othersRow.push(value > 0 ? value : "");
        rowTotal += value;
        countColumns[i] += value;
      }
      if (hasOthersColumn) {
        othersRow.push(othersCorner > 0 ? othersCorner : "");
        rowTotal += othersCorner;
        countColumns[valueColumns] += othersCorner;
      }
      othersRow.push(rowTotal);
      countColumns[countColumns.length - 1] += rowTotal;
      dataTable.push(othersRow);
    }

    dataTable.push(countColumns);
    dataTable = [...dataTable];
    cellTooltips = nextCellTooltips;
    drilledFrom = selectedFrom;
    drilledTo = selectedTo;
    updateValueHighlights();
  }

  function filterShortcuts() {
    if (selectedTo === "Shortcut/App") {
      selectedFrom = "";
      selectedTo = "";
      shortcutTable();
      return;
    }

    let previousTableHeader = [...tableHeader];
    let previousDataTable = [...dataTable];
    let matches = [];
    let rowKeys = [];
    tableHeader = ["Shortcut/App"];
    dataTable = [];

    // Option 1: only column selected (shortcut)
    if (selectedTo && !selectedFrom) {
      const selectedApp = selectedTo;

      data.forEach((entry) => {
        if (entry.app === selectedApp) {
          if (!tableHeader.includes(entry.windowtitle)) {
            tableHeader.push(entry.windowtitle);
          }
          if (!rowKeys.includes(entry.shortcut)) {
            rowKeys.push(entry.shortcut);
          }
          matches.push({
            rowKey: entry.shortcut,
            colKey: entry.windowtitle,
            count: entry.count,
          });
        }
      });
    }

    // Option 2: only row selected (shortcut)
    else if (selectedFrom && !selectedTo) {
      const selectedShortcut = selectedFrom;

      data.forEach((entry) => {
        if (entry.shortcut === selectedShortcut) {
          if (!tableHeader.includes(entry.app)) {
            tableHeader.push(entry.app);
          }
          if (!rowKeys.includes(entry.windowtitle)) {
            rowKeys.push(entry.windowtitle);
          }
          matches.push({
            rowKey: entry.windowtitle,
            colKey: entry.app,
            count: entry.count,
          });
        }
      });
    }

    // Option 3: cell selected (shortcut + app)
    else if (selectedFrom && selectedTo) {
      const selectedShortcut = selectedFrom;
      const selectedApp = selectedTo;

      data.forEach((entry) => {
        if (entry.shortcut === selectedShortcut && entry.app === selectedApp) {
          if (!tableHeader.includes(entry.windowtitle)) {
            tableHeader.push(entry.windowtitle);
          }
          if (!rowKeys.includes(entry.shortcut)) {
            rowKeys.push(entry.shortcut);
          }
          matches.push({
            rowKey: entry.shortcut,
            colKey: entry.windowtitle,
            count: entry.count,
          });
        }
      });
    }

    if (matches.length === 0) {
      dataTable = previousDataTable;
      tableHeader = previousTableHeader;
      updateValueHighlights();
      return;
    }

    tableHeader.push("Total");
    let countColumns = ["Total", ...Array(tableHeader.length - 2).fill(0), 0];

    rowKeys.forEach((rowKey) => {
      let count = 0;
      let row = [rowKey];
      for (let i = 1; i < tableHeader.length - 1; i++) {
        const colKey = tableHeader[i];
        const valueList = matches.filter(
          (item) => item.rowKey === rowKey && item.colKey === colKey,
        );
        const c = valueList.reduce((sum, item) => sum + item.count, 0);
        row.push(c > 0 ? c : "");
        count += c;
        countColumns[i] += c;
      }
      row.push(count);
      countColumns[countColumns.length - 1] += count;
      dataTable.push(row);
    });

    dataTable.push(countColumns);
    dataTable = [...dataTable];
    updateValueHighlights();
  }

  function filterDataTable() {
    if (selectedIndex === 0) {
      fiterCopyPaste();
    } else {
      currentApp = selectedTo;
      filterShortcuts();
    }
  }

  function getApplist(applist) {
    return applist
      .map((app) => applicationlist.find((a) => a.clearname === app))
      .filter((app) => app !== undefined);
  }

  function getValueHighlights(rows) {
    const values = [];
    const emptyHighlights = { warningCells: new Set(), highCells: new Set() };

    // Summen- und Sammelzeile sind Buendel, keine Messwerte - sie faerben
    // die Verteilung ein und bekommen selbst keine Einfaerbung.
    const isBundle = (row, i) => i === rows.length - 1 || row[0] === OTHERS;

    rows.forEach((row, i) => {
      if (isBundle(row, i)) return;
      row.forEach((cell, j) => {
        if (j === 0 || j === row.length - 1 || !Number.isFinite(cell)) return;
        values.push(cell);
      });
    });

    if (values.length < 5) return emptyHighlights;

    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const variance =
      values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      values.length;
    const stdDev = Math.sqrt(variance);

    const highThreshold = mean + 2 * stdDev;
    const warningCells = new Set();
    const highCells = new Set();

    rows.forEach((row, i) => {
      if (isBundle(row, i)) return;
      row.forEach((cell, j) => {
        if (
          j !== 0 &&
          j !== row.length - 1 &&
          Number.isFinite(cell) &&
          cell > mean
        ) {
          warningCells.add(`${i}:${j}`);
        }
        if (
          stdDev > 0 &&
          j !== 0 &&
          j !== row.length - 1 &&
          Number.isFinite(cell) &&
          cell >= highThreshold
        ) {
          highCells.add(`${i}:${j}`);
        }
      });
    });

    return { warningCells, highCells };
  }

  function updateValueHighlights() {
    const valueHighlights =
      selectedIndex === 0
        ? getValueHighlights(dataTable)
        : { warningCells: new Set(), highCells: new Set() };
    warningValueCells = valueHighlights.warningCells;
    highValueCells = valueHighlights.highCells;
  }

  // Fenstertitel-Ebene der Copy-Paste-Matrix: dort und nur dort greift die
  // Schwelle, auf der Anwendungsebene gibt es nichts auszuduennen.
  // Erst zeigen, wenn eine Schwelle berechnet ist - sonst haengt sich der
  // Regler mit leerem Wert ein und schreibt den ueber die Bindung zurueck.
  $: onWindowLevel =
    selectedIndex === 0 &&
    Boolean(selectedFrom || selectedTo) &&
    Number(windowMinCount) > 0;

  function applyWindowMinCount() {
    windowMinTouched = true;
    fiterCopyPaste();
  }

  function handleMouseDown() {
    if (selectedIndex === 0) {
      clearTimeout(frequencyDebounceTimeout);
      copyPasteTable();
      return;
    }
    applyFrequencyDebounced();
  }

  function applyFrequencyDebounced() {
    const nextFrequency = frequency;
    clearTimeout(frequencyDebounceTimeout);
    frequencyDebounceTimeout = setTimeout(() => {
      if (appliedFrequency !== nextFrequency) {
        appliedFrequency = nextFrequency;
      }
    }, 500);
  }

  function formatPaxbotDuration(value) {
    const totalSeconds = Math.max(0, Math.round(Number(value) || 0));
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }

  function formatPercentage(value) {
    return String(Math.round(Number(value) || 0));
  }

  function getCopyTimeSummary(apps = []) {
    const copyTimes = apps
      .map((app) => Math.max(0, Number(app.copy_time) || 0))
      .sort((left, right) => right - left);
    const totalCopyTime = copyTimes.reduce((sum, value) => sum + value, 0);
    const totalTime = apps.reduce(
      (sum, app) => sum + Math.max(0, Number(app.time) || 0),
      0,
    );
    const coverage = (count) =>
      totalCopyTime > 0
        ? (copyTimes.slice(0, count).reduce((sum, value) => sum + value, 0) /
            totalCopyTime) *
          100
        : 0;
    return {
      totalCopyTime,
      averageCopyTimeShare:
        totalTime > 0 ? (totalCopyTime / totalTime) * 100 : 0,
      top10CopyTimeShare: coverage(10),
    };
  }

  function getCopyGraph(
    connections = [],
    level = 0,
    kpiData = [],
    selectedNode = "",
  ) {
    const nodeId = (item, direction) =>
      level === 1
        ? `${item[`${direction}_app`]} - ${item[`${direction}_window`]}`
        : item[`${direction}_app`];
    const edgesByKey = new Map();
    const nodeIds = new Set();
    connections.forEach((connection) => {
      const from = nodeId(connection, "from");
      const to = nodeId(connection, "to");
      if (!from || !to) return;
      if (selectedNode && from !== selectedNode && to !== selectedNode) return;
      nodeIds.add(from);
      nodeIds.add(to);
      const key = `${from}\x1f${to}`;
      edgesByKey.set(key, {
        from,
        to,
        value:
          (Number(edgesByKey.get(key)?.value) || 0) +
          (Number(connection.count) || 0),
      });
    });
    const kpisById = new Map(kpiData.map((item) => [String(item.id), item]));
    if (selectedNode && kpisById.has(String(selectedNode))) {
      nodeIds.add(selectedNode);
    }
    return {
      nodes: [...nodeIds].map((id) => {
        const item = kpisById.get(String(id)) || {};
        const relatedConnections = connections.filter(
          (connection) =>
            nodeId(connection, "from") === id ||
            nodeId(connection, "to") === id,
        );
        const detail = getCopyPasteTooltip(relatedConnections, level);
        return {
          id,
          label: id,
          weighted_time: Number(item.copy_time) || 0,
          detailTitle: detail.title,
          details: detail.rows,
          image: item.applicationname
            ? `/api/damprojects/app-icon?application=${encodeURIComponent(item.applicationname)}`
            : "",
        };
      }),
      edges: [...edgesByKey.values()],
    };
  }

  function toggleAnalysisView() {
    analysisView = analysisView === "table" ? "flow" : "table";
    if (analysisView === "table") {
      selectedLevel = 0;
      currentApp = "";
      selectedFrom = "";
      selectedTo = "";
    }
  }

  function getGlobalKpiContext() {
    return {
      totalCopyTime: formatPaxbotDuration(copyTimeSummary.totalCopyTime),
      averageCopyTimeShare: `${formatPercentage(copyTimeSummary.averageCopyTimeShare)}%`,
      copyUserCoverage: `${formatPercentage(copyUserCoverage)}%`,
      top10CopyTimeShare: `${formatPercentage(copyTimeSummary.top10CopyTimeShare)}%`,
    };
  }

  function getMatrixConnections(question = "") {
    const headers = tableHeader || [];
    const rows = (dataTable || []).slice(0, -1);
    const connections = [];
    rows.forEach((row) => {
      for (
        let columnIndex = 1;
        columnIndex < headers.length - 1;
        columnIndex++
      ) {
        const count = Number(row[columnIndex]) || 0;
        if (count > 0) {
          connections.push({
            from: row[0],
            to: headers[columnIndex],
            count,
          });
        }
      }
    });
    const normalizedQuestion = String(question).toLowerCase();
    const relevant = connections.filter(
      (connection) =>
        normalizedQuestion.includes(String(connection.from).toLowerCase()) ||
        normalizedQuestion.includes(String(connection.to).toLowerCase()) ||
        (selectedFrom && selectedFrom === connection.from) ||
        (selectedTo && selectedTo === connection.to),
    );
    const strongest = [...connections].sort(
      (left, right) => right.count - left.count,
    );
    const unique = new Map();
    [...relevant, ...strongest].forEach((connection) => {
      const key = `${connection.from}\x1f${connection.to}`;
      if (!unique.has(key)) unique.set(key, connection);
    });
    return {
      totalConnectionCount: connections.length,
      connections: [...unique.values()].slice(0, 40),
    };
  }

  function getPaxbotContext(question = "") {
    const headers = tableHeader || [];
    const rows = dataTable || [];
    const totalRow = rows[rows.length - 1] || [];
    const matrixConnections = getMatrixConnections(question);
    return {
      page: "Copy-Paste Analysis",
      projectId: idproject,
      source: dataSource(),
      view: analysisView,
      aggregationLevel: selectedLevel === 1 ? "windows" : "applications",
      activeFilterCount: getCurrentFilters().length,
      minimumFrequency: appliedFrequency,
      selection: {
        application: currentApp || null,
        from: selectedFrom || null,
        to: selectedTo || null,
      },
      globalKpis: getGlobalKpiContext(),
      flow: {
        nodes: copyGraph.nodes.slice(0, 40).map((node) => ({
          name: node.label,
          copyTime: formatPaxbotDuration(node.weighted_time),
        })),
        transitions: [...copyGraph.edges]
          .sort((left, right) => right.value - left.value)
          .slice(0, 40)
          .map((edge) => ({
            from: edge.from,
            to: edge.to,
            copyOperations: edge.value,
          })),
      },
      matrix: {
        rowNames: rows.slice(0, -1).map((row) => row[0]),
        columnNames: headers.slice(1, -1),
        rowTotals: rows.slice(0, -1).map((row) => ({
          name: row[0],
          total: Number(row[row.length - 1]) || 0,
        })),
        columnTotals: headers.slice(1, -1).map((name, index) => ({
          name,
          total: Number(totalRow[index + 1]) || 0,
        })),
        grandTotal: Number(totalRow[totalRow.length - 1]) || 0,
        totalConnectionCount: matrixConnections.totalConnectionCount,
        visibleConnections: matrixConnections.connections,
      },
    };
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      signal: abortSignal,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: history.slice(-2),
        context: {
          ...getPaxbotContext(question),
          responseLanguage,
        },
      }),
    });
    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch {
      throw new Error(
        `Paxbot endpoint returned ${response.status || "an invalid response"}`,
      );
    }
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }

  $: frequency, applyFrequencyDebounced();

  onDestroy(() => {
    clearTimeout(frequencyDebounceTimeout);
  });
</script>

<Header
  paddingRight={false}
  section="Usage Analysis"
  area="Usage Analysis"
  breadcrumb="Copy-Paste Analysis"
  tooltipText="The Copy-Paste Analysis highlights recurring copy-and-paste patterns across applications and windows."
>
  <AppDrilldownSourceMenu bind:selected={selectedSourceIndex} />
  <Filter />
</Header>

<SectionWrapper
  fullscreen={fullscreen && !noDataForFilters}
  fitScreen={analysisView === "flow" && !noDataForFilters}
  fitContent={analysisView !== "flow" && !noDataForFilters}
  loading={loading || loadingApp}
  loadingTitle="Loading shortcut data."
  loadingHint="Please wait while Copy-Paste Analysis is prepared."
  emptyState={noDataForFilters}
>
  <div
    slot="main"
    class="main_wapper"
    class:flow_mode={analysisView === "flow"}
  >
    <PaxbotChat
      bind:open={paxbotOpen}
      heading="Pax AI"
      subheading="Copy-Paste Analysis Assistant"
      requestAnswer={requestPaxbotAnswer}
    />
    <div class="graph_top_right_toolbar">
      <Button
        kind={paxbotOpen ? "primary" : "secondary"}
        icon={BOT_WHITE_ICON}
        iconDescription="Pax AI"
        on:click={() => (paxbotOpen = !paxbotOpen)}
      />
    </div>
    <div class="graph_left_toolbar">
      <Button
        kind="secondary"
        icon={analysisView === "table" ? TRANSITIONS_ICON : TABLE_ICON}
        iconDescription={analysisView === "table"
          ? "Show copy flow"
          : "Show copy matrix"}
        on:click={toggleAnalysisView}
      />
    </div>
    <div class="canvas" class:flow_canvas={analysisView === "flow"}>
      <div class="ui_viewer_wrapper">
        {#if loading}
          <div class="loading_wrapper">
            <Loading withOverlay={false} />
          </div>
        {:else if analysisView === "flow"}
          <FlowPlotterSmall
            dottedBackground
            nodes={copyGraph.nodes}
            edges={copyGraph.edges}
            width="100%"
            height="100%"
            formatEdges={(value) => value}
            formatNodeMetric={formatPaxbotDuration}
            nodeMetricLabel="copy time"
            bind:selectedNode={currentApp}
            bind:zoomLevel
          />
        {:else}
          <MatrixTable
            marginBottom="5vh"
            header={tableHeader}
            rows={dataTable}
            {loading}
            bind:to={selectedTo}
            bind:from={selectedFrom}
            disableFinalField
            summaryLastRow
            variant="modern"
            horizontalScroll
            warningCells={warningValueCells}
            highlightCells={highValueCells}
            {cellTooltips}
          />
        {/if}
      </div>
    </div>
    <div class="menu_overlay bottom">
      <div class="buttons_wrapper">
        {#if analysisView === "flow" && !windowTitlesLocked}
          <ContentSwitcher
            bind:selected={selectedLevel}
            options={["Applications", "Windows"]}
            fitContent={true}
          />
        {/if}
        {#if selectedIndex === 0}
          {#if onWindowLevel}
            <ButtonNumberInput
              buttonText="Min. interactions"
              bind:value={windowMinCount}
              handleMouseDown={applyWindowMinCount}
            />
          {:else}
            <!-- Anwendungsebene: die Mindesthaeufigkeit waehlt der Mensch selbst
                 (Filter in copyPasteTable(): Summe je Von/Nach-Paar >= frequency). -->
            <ButtonNumberInput
              buttonText="Min. interactions"
              bind:value={frequency}
              {handleMouseDown}
            />
          {/if}
        {:else}
          <ButtonNumberInput
            buttonText="Min. interactions"
            bind:value={frequency}
            {handleMouseDown}
          />
        {/if}
      </div>

      <div class="buttons_wrapper">
        <Button
          kind="secondary"
          icon={fullscreen ? FULLSCREEN_OFF_ICON : FULLSCREEN_ICON}
          on:click={() => {
            fullscreen = !fullscreen;
          }}
        />
      </div>
    </div>
  </div>

  <div slot="emptyState" class="empty_state_text">
    <div class="empty_state_title">
      No data is available for this view with the selected filters.
    </div>
    <div class="empty_state_hint">
      Remove or adjust the filters to update the results.
    </div>
  </div>

  <div class="menu" slot="menu">
    <Accordion
      heading="App Selection"
      open={true}
      tooltipText="Select an application to filter the matrix to copy-and-paste interactions originating from it. Select the same application again to clear the filter."
    >
      <AppSelection
        appData={selectionData}
        bind:currentApp
        bind:selectedLevel
        windowSelectionEnabled={false}
        disableBelowOnePercent={true}
        enableDeselection={analysisView !== "flow"}
        kpi="copy_time"
        showKpiSummary
        kpiSummaryItems={copyTimeSummaryItems}
        readableDuration
        loading={loadingApp}
      />
    </Accordion>
    <!-- <Accordion heading="App Information" open={true}>
      <AppInformation
        summary
        appData={applicationlist}
        {currentApp}
        loading={loadingApp}
      />
    </Accordion> -->

    <!-- <Accordion heading="Settings" open={false}>
      <div class="toggle_wrapper">
        <Toggle text="UI-Elements" bind:active={drawUIElements} />
        <Toggle text="Clusters" bind:active={drawClicks} />
        <Toggle text="Screenshot" bind:active={drawScreenshot} />
        <div class="upload_wrapper">
          <Button
            setting={true}
            variant="secondary"
            iconLeft={true}
            enlarge={true}
            icon="upload"
            text="Upload Screenshot"
            disable={true}
          />
        </div>
      </div>
    </Accordion> -->
  </div>
</SectionWrapper>

<style>
  /* .upload_wrapper {
    padding-top: 1rem;
    margin-top: 1rem;
    border-top: 1px solid #d9d9d9;
  }

  .toggle_wrapper {
    display: flex;
    flex-direction: column;
  } */

  .main_wapper {
    position: relative;
    height: auto;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .empty_state_title,
  .empty_state_hint {
    font-size: 1rem;
  }

  .empty_state_title {
    color: var(--pax-black);
  }

  .empty_state_hint {
    color: var(--pax-grey);
    margin-top: 0.5rem;
  }

  .main_wapper.flow_mode {
    height: 100%;
    gap: 0;
  }

  .main_wapper.flow_mode .menu_overlay {
    bottom: 0;
    position: absolute;
  }

  .main_wapper :global(.paxbot_chat_window) {
    bottom: 5.85rem !important;
    position: absolute !important;
    right: 1rem !important;
    z-index: 50 !important;
  }

  .main_wapper :global(.paxbot_chat_mascot) {
    bottom: -0.65rem !important;
    position: absolute !important;
    right: 1.55rem !important;
    z-index: 51 !important;
  }

  .menu_overlay {
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    gap: 3rem;
    align-items: center;
  }

  .bottom {
    margin-top: 0.5rem;
    /* Auf der Fenstertitel-Ebene wird die Tabelle lang und schob die Leiste
       aus dem Bild. Sie bleibt jetzt am unteren Rand stehen. */
    position: sticky;
    bottom: 0.5rem;
  }

  .canvas {
    height: auto;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 4rem;
    box-sizing: border-box;
  }

  .flow_canvas {
    bottom: 4rem;
    height: auto;
    left: 0;
    min-height: 0;
    padding: 0;
    position: absolute;
    right: 0;
    top: 4rem;
    width: auto;
    align-items: center;
  }

  .flow_canvas .ui_viewer_wrapper {
    height: 100%;
  }

  .graph_left_toolbar {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 4;
  }

  .graph_top_right_toolbar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 4;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-end;
  }

  .buttons_wrapper {
    display: flex;
    gap: 0.5rem;
    user-select: none;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100vh - 6rem);
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .ui_viewer_wrapper {
    min-height: 100%;
    width: 100%;
    max-width: 100%;
    overflow: visible;
  }

  .loading_wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80vh;
    width: 100%;
  }
</style>
